import { ExperienceDateService } from '@app/onboarding/services/experience-date.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { TagsApi } from '@app/tags/tag-api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { InputsService } from '@app/inputs/services/inputs.service';
import { Experiences, ProfileCollectionItem } from '@app/profile/profile-api';
import { UserPosition } from '@app/inputs/inputs-api';
import { Profile } from '@app/profile/profile';
import { InputModalDispatcherService } from '@app/user-content/user-input/services/input-modal-dispatcher.service';
import { ProfileExperiencesService } from './profile-experiences.service';

@Component({
  selector: 'dgx-profile-experiences-v2',
  templateUrl: './profile-experience.component.html',
  styleUrls: ['./profile-experience.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileExperienceComponentV2
  extends SubscriberBaseDirective
  implements OnInit, OnChanges
{
  // Bindings
  @Input() public profile: Profile;
  @Input() public ownerIsViewing: boolean;
  @Input() public isReadOnly = false;
  @Input() public experiencesDisplayLimit: number;
  @Input() public showViewAll: boolean = true;
  @Input() public showAdd: boolean = true;
  @Input() public tag: TagsApi.Tag;
  // This can be used broadly to show all experiences, or experiences for a specific tag
  // if a tag is required, make sure to wait for the tag to be available before fetching experiences
  @Input() public requiresTag: boolean;
  @Input() public isPageLoading: boolean;
  @Input() public useLoadingTemplate: boolean;
  @Input() public useShowMore: boolean;
  @Input() public pageType: string;

  @Input() public userLoggedIn: boolean;

  @Output() public itemEdited = new EventEmitter<Event>();
  @Output() public experienceDeleted = new EventEmitter<Event>();
  @Output() public experiencesLoading = new EventEmitter<{
    type: string;
    status: boolean;
  }>();

  public experiences: ProfileCollectionItem<UserPosition>[] = [];
  public isEmpty: boolean;
  public isOpen: { [id: string]: boolean } = {};
  public viewAllUrl: string;
  public viewAllUrlQueryParams: { classifier: string };
  public skillLimit: number = 20;
  public hasMoreItems: boolean;
  public sectionLoading = true;
  public initialLoad: boolean;
  public totalCount: number = 0;
  public changesFired: boolean = false;

  public events: Subscription[] = [];

  public i18n = this.translateService.instant([
    'Core_Add',
    'Core_Edit',
    'Core_Delete',
    'Core_AddTags',
    'Core_ExperiencesDisplayName',
    'Core_GeneralErrorMessage',
    'Core_ReportAProblem',
    'ProfileCtrl_ExperienceDatePresent',
    'ProfileCtrl_ExperienceReported',
    'ProfileCtrl_EditCollectionItemNotifierFormat',
    'Core_Skills',
    'Core_Skill',
    'Core_ViewAll',
    'confirmModal_DeletePositionTitle',
    'confirmModal_DeletePositionBody',
    'dgProfileOverview_OverviewNotProvidedTitle',
    'dgProfileOverview_AddExperiences',
    'dgProfileOverview_AddDescription',
    'Core_ShowMore',
    'Opportunities_Error_AddExperience',
  ]);

  constructor(
    private translateService: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
    private inputsService: InputsService,
    private profileExperienceService: ProfileExperiencesService,
    private genericModalService: InputModalDispatcherService,
    private experienceDateService: ExperienceDateService
  ) {
    super();
    this.inputsService.inputModify
      .pipe(this.takeUntilDestroyed())
      .subscribe((contentType) => {
        if (contentType === 'Position') {
          this.getExperiences(true, false);
        }
      });
  }

  public ngOnInit(): void {
    const ownerUrl = `/${this.profile.vanityUrl}`;
    this.viewAllUrl = `/profile${ownerUrl}/collection`;
    this.viewAllUrlQueryParams = { classifier: 'experiences' };

    this.setLoadingState(true);
    // if statement stops the ngOnInit from calling getExperiences a second time since the ngOnChanges is firing first
    if (!this.changesFired) this.getExperiences(true, false);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.pageType || changes?.tag) {
      this.changesFired = true;
      this.getExperiences(true, false);
    }
  }

  public getExperiences(initial = false, loadMore = false): void {
    if (this.requiresTag && !this.tag) {
      return;
    }
    this.sectionLoading = true;
    if (initial) {
      this.initialLoad = true;
    } else {
      this.itemEdited?.emit();
    }

    const numToLoad = this.getNumberToLoad(loadMore);

    const skipNum = loadMore ? this.experiences.length : 0;

    this.profileExperienceService
      .getPublicSortedUserPositions(
        this.profile.id,
        numToLoad,
        skipNum,
        this.tag?.tagId
      )
      .pipe(this.takeUntilDestroyed())
      .subscribe((experiences: Experiences) => {
        if (experiences.succeeded) {
          const experienceItems = this.getExperiencesAsProfileCollectionItems(
            experiences?.items
          );
          this.experiences = loadMore
            ? [...this.experiences, ...experienceItems]
            : experienceItems;

          this.hasMoreItems = experiences?.hasMoreItems;
          this.totalCount = experiences?.totalCount;
        } else {
          // GetSortedPublicUserPositions returns a non-standard response when there are no experiences,
          // this should only happen when a user has no experiences / has just deleted their last experience
          this.experiences = [];
          this.hasMoreItems = false;
          this.totalCount = 0;
        }
        this.isEmpty = !this.experiences.length;
        this.setLoadingState(false);
        this.initialLoad = false;
        this.changesFired = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  /**
   * Determines if the details toggle should appear
   *
   * @param experience - the experience object.
   */
  public canToggleDetailsSection(experience: UserPosition): boolean {
    return !!(
      this.ownerIsViewing ||
      (!this.ownerIsViewing &&
        (experience.description || experience.tags?.length))
    );
  }

  /**
   * Determines if the add description link should appear
   *
   * @param description - the description of the experience
   */
  public canAddDescription(description: string): boolean {
    return !description && this.ownerIsViewing;
  }

  /**
   * Determines if the add skills link should appear
   *
   * @param skills - the description of the experience
   */
  public canAddSkills(skills: TagsApi.Tag[]): boolean {
    return !skills?.length && this.ownerIsViewing;
  }

  /**
   * Create a list of all the visible skills
   *
   * @param tags - the list of tags.
   */
  public getVisibleSkills(tags: TagsApi.Tag[]): TagsApi.Tag[] {
    return tags.slice(0, this.skillLimit);
  }

  /**
   * Create a label with a total count of the number of skills
   *
   * @param tags - the list of tags.
   */
  public getPillSkillCount(tags: TagsApi.Tag[]): string {
    const plural =
      tags.length === 1 ? this.i18n.Core_Skill : this.i18n.Core_Skills;
    return `${tags.length} ${plural}`;
  }

  /**
   * Create a label with a count of the number of skills greater than the skillLimit value
   *
   * @param tags - the list of tags.
   */
  public getHiddenSkills(tags: TagsApi.Tag[]): string {
    return `+ ${tags.length - this.skillLimit}`;
  }

  /**
   * Toggle the details section
   *
   * @param id - the id of the experience.
   */
  public toggleDetails(id: number): void {
    this.isOpen[id] = !this.isOpen[id];
  }

  /**
   * Sets a label of the date duration of the experience and along with the months and years
   *
   * @param experience - the experience object.
   */
  public buildDate(experience: UserPosition): string {
    return this.experienceDateService.buildDate(experience);
  }

  /**
   * Returns a formatted string for organization name and date so that the ellipsis in the view
   * can truncate the text accordingly
   *
   * @param experience - the experience object.
   */
  public getSubHeading(experience: UserPosition): string {
    // TODO: Remove line once BE EP updated
    if (!experience.organizationName) return '';
    return `${experience.organizationName} \u2022 ${this.buildDate(
      experience
    )}`;
  }

  /**
   * Opens the edit experience modal
   *
   * @param experience - the experience object.
   */
  public editExperience(experience: UserPosition): void {
    this.genericModalService
      .edit({
        inputId: experience.inputId,
        userInputId: experience.userInputId,
        inputType: 'Position',
        initialModel: {
          inputId: experience.inputId,
          userInputId: experience.userInputId,
          inputType: 'Position',
        },
      })
      .pipe(this.takeUntilDestroyed())
      .subscribe((result) => {
        // if (!result) {
        //   return;
        // }
        //
        // this.getExperiences(false, false);
      });
  }

  /**
   * Opens the add experience modal
   */
  public addExperience(event: Event): void {
    this.genericModalService
      .complete({
        inputType: 'Position',
      })
      .pipe(this.takeUntilDestroyed())
      .subscribe((result) => {});
  }

  private getNumberToLoad(loadMore: boolean) {
    let numToLoad;

    if (loadMore) {
      const difference = this.totalCount - this.experiences.length;
      numToLoad =
        difference >= this.experiencesDisplayLimit
          ? this.experiencesDisplayLimit
          : difference;
    } else {
      numToLoad = this.experiencesDisplayLimit;
    }
    return numToLoad;
  }

  private setLoadingState(status: boolean) {
    this.sectionLoading = status;
    this.experiencesLoading.emit({ type: 'experiences', status });
  }

  private getExperiencesAsProfileCollectionItems(
    experiences: UserPosition[]
  ): ProfileCollectionItem<UserPosition>[] {
    return experiences.map((experience) => {
      const {
        inputType,
        userInputId: userItemId,
        inputId: itemId,
      } = experience;
      return {
        contentType: 'experience',
        content: experience,
        inputType,
        userItemId,
        itemId,
        contentCategory: '',
      };
    });
  }
}
